import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Save, Trash2, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, mensajeError } from '../lib/api';
import { olvidarMarcas } from '../lib/marcas';
import { SectionCard } from './Common';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import MarcaLead from './MarcaLead';

/**
 * Configuración → Marcas (solo admin). Las marcas de la clínica, los
 * tratamientos de sus campañas y cómo se escriben las respuestas del
 * formulario de Meta: todo vive en la base, nada en el código. El servidor
 * apunta cada cambio en Control rastreo (qué había y qué queda).
 *
 * Cada lista se escribe una cosa por línea. Las palabras que reconocen una
 * marca o un tratamiento casan como palabras enteras, sin tildes ni
 * mayúsculas; entre barras (/…/) son una expresión regular.
 *
 * «Remitentes de los formularios web» (28/09/2026): desde qué dominio o
 * dirección llegan al buzón los formularios de la web de cada marca. Si uno
 * pide cita, el lead se crea solo en Leads (services/leads_desde_correo.py).
 * Vacía, no se crea ninguno: no es obligatoria y no sale en «lo que falta».
 *
 * «Mensajes al paciente» (29/09/2026): la dirección y el teléfono que leen los
 * pacientes en la confirmación y el recordatorio de las citas de las agendas
 * que son de esta marca (Gestión de agendas → Configurar agenda → Marca). El
 * nombre sale del nombre largo y las iniciales. Vacíos, los de Configuración.
 *
 * «Membrete de los documentos» (26/10/2026): el lema, la web, la dirección y
 * el teléfono (tal cual, con su etiqueta) de los documentos y la línea del pie.
 * Con el logotipo y los colores de la marca, el membrete de los papeles de sus
 * médicos (services/membrete.py en el servidor). Vacíos, no salen.
 *
 * «Sus contactos no se apuntan en Leads» (01/11/2026, dirección: «los de [un
 * portal] elimínalos, no los adjuntes»): solo en una procedencia externa.
 * Marcada, un contacto nuevo que saldría como de ella no entra en Leads, lo
 * mande quien lo mande (la pantalla, el conector, un formulario web). Lo que
 * ya está en Leads no se toca.
 */

const lineas = (v) => (Array.isArray(v) ? v : []).join('\n');
const deLineas = (t) => String(t || '').split('\n').map((x) => x.trim()).filter(Boolean);

// Las listas de «Respuestas del formulario de Meta», una por pregunta: cómo se
// llaman en el servidor (el documento `formulario_meta`) y cómo aquí. La de
// «qué quiere mejorar» es del 28/09/2026; las dos de la valoración (cómo la
// prefiere y si le parece bien pagarla), del 30/09/2026; el plazo (cuándo
// quiere operarse), del 02/10/2026. Las dos del permiso para ofertas
// (12/10/2026) dicen además qué SIGNIFICA cada respuesta: las de la primera
// son un «sí» (entra en la lista de ofertas de Marketing) y las de la segunda,
// un «no». Sin ellas, el servidor lee «Sí…» como sí y «No…» como no.
const LISTAS_FORMULARIO_META = [
  { clave: 'tratamientos', etiqueta: 'Tratamiento' },
  { clave: 'presupuestos', etiqueta: 'Presupuesto' },
  { clave: 'mejorar', etiqueta: 'Qué quiere mejorar' },
  { clave: 'modalidad_valoracion', etiqueta: 'Cómo prefiere la valoración' },
  { clave: 'pago_valoracion', etiqueta: 'Pago de la valoración' },
  { clave: 'plazo', etiqueta: 'Plazo (cuándo quiere operarse)' },
  { clave: 'ofertas_si', etiqueta: 'Ofertas: respuestas que dicen «sí»' },
  { clave: 'ofertas_no', etiqueta: 'Ofertas: respuestas que dicen «no»' },
];
const FORMULARIO_META_VACIO = Object.fromEntries(LISTAS_FORMULARIO_META.map(({ clave }) => [clave, '']));

const MARCA_VACIA = {
  clave: '', nombre: '', nombre_largo: '', nombre_corto: '', del: '', mencion: '',
  color: '#475569', color_texto: '', color_punto: '', externa: false, sin_leads: false, buzon: '',
  alias: '', dominios: '', remitentes_web: '', paginas_facebook: '', formularios_meta: '',
  cuentas_leer: '', cuentas_marca: '', orden: 0, activa: true,
  direccion_citas: '', telefono_citas: '',
  lema: '', web: '', direccion_documentos: '', telefono_documentos: '', pie_documentos: '',
};

function aFormulario(m) {
  if (!m) return { ...MARCA_VACIA };
  const cuentas = Array.isArray(m.cuentas_anuncios) ? m.cuentas_anuncios : [];
  return {
    ...MARCA_VACIA, ...m,
    alias: lineas(m.alias), dominios: lineas(m.dominios), remitentes_web: lineas(m.remitentes_web),
    paginas_facebook: lineas(m.paginas_facebook), formularios_meta: lineas(m.formularios_meta),
    cuentas_leer: cuentas.filter((c) => c.sincronizar).map((c) => c.id).join('\n'),
    cuentas_marca: cuentas.filter((c) => !c.sincronizar).map((c) => c.id).join('\n'),
  };
}

function aServidor(f) {
  return {
    clave: f.clave.trim(), nombre: f.nombre.trim(), nombre_largo: f.nombre_largo.trim(),
    nombre_corto: f.nombre_corto.trim(), del: f.del.trim(), mencion: f.mencion.trim(),
    color: f.color.trim(), color_texto: f.color_texto.trim(), color_punto: f.color_punto.trim(),
    externa: Boolean(f.externa), buzon: f.externa ? '' : f.buzon.trim(),
    // Solo una procedencia externa puede cerrar la puerta a sus contactos.
    sin_leads: Boolean(f.externa) && Boolean(f.sin_leads),
    alias: deLineas(f.alias), dominios: deLineas(f.dominios),
    remitentes_web: deLineas(f.remitentes_web),
    paginas_facebook: deLineas(f.paginas_facebook), formularios_meta: deLineas(f.formularios_meta),
    cuentas_anuncios: [
      ...deLineas(f.cuentas_leer).map((id) => ({ id, sincronizar: true })),
      ...deLineas(f.cuentas_marca).map((id) => ({ id, sincronizar: false })),
    ],
    orden: Number(f.orden) || 0, activa: Boolean(f.activa),
    // Una procedencia externa no firma los mensajes de la clínica.
    direccion_citas: f.externa ? '' : String(f.direccion_citas || '').trim(),
    telefono_citas: f.externa ? '' : String(f.telefono_citas || '').trim(),
    // El membrete de los documentos (26/10/2026): una procedencia externa
    // no firma documentos de la clínica.
    ...Object.fromEntries(CAMPOS_MEMBRETE.map((k) => [k, f.externa ? '' : String(f[k] || '').trim()])),
  };
}

// Lo que la marca pone en el membrete de los documentos (26/10/2026,
// services/membrete.py): presupuestos, informes, justificantes,
// consentimientos, pruebas preoperatorias, indicaciones y postoperatorios.
const CAMPOS_MEMBRETE = ['lema', 'web', 'direccion_documentos', 'telefono_documentos', 'pie_documentos'];

function Campo({ id, etiqueta, children, ayuda }) {
  return (
    <div>
      <Label htmlFor={id}>{etiqueta}</Label>
      {children}
      {ayuda ? <p className="text-xs text-muted-foreground mt-0.5">{ayuda}</p> : null}
    </div>
  );
}

function FormularioMarca({ marca, onGuardada, onCancelar }) {
  const nueva = !marca?.id;
  const [f, setF] = useState(() => aFormulario(marca));
  const [guardando, setGuardando] = useState(false);
  const pon = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const texto = (k, etiqueta, props = {}) => (
    <Campo id={`marca-${k}`} etiqueta={etiqueta} ayuda={props.ayuda}>
      <Input id={`marca-${k}`} value={f[k]} onChange={pon(k)} data-testid={`marca-form-${k}`}
        disabled={props.disabled} placeholder={props.placeholder} />
    </Campo>
  );
  const lista = (k, etiqueta, ayuda) => (
    <Campo id={`marca-${k}`} etiqueta={etiqueta} ayuda={ayuda}>
      <Textarea id={`marca-${k}`} rows={3} value={f[k]} onChange={pon(k)} data-testid={`marca-form-${k}`} />
    </Campo>
  );
  const guardar = async () => {
    setGuardando(true);
    try {
      if (nueva) await api.post('/api/marcas', aServidor(f));
      else await api.put(`/api/marcas/${marca.id}`, aServidor(f));
      toast.success(nueva ? 'Marca dada de alta' : 'Marca guardada');
      olvidarMarcas();
      await onGuardada();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar la marca.'));
    } finally {
      setGuardando(false);
    }
  };
  return (
    <div className="rounded-md border p-3 space-y-3" data-testid="marca-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {texto('clave', 'Clave', { disabled: !nueva, placeholder: 'alfa',
          ayuda: 'Corta, sin espacios. No se cambia: se guarda en los leads y las campañas.' })}
        {texto('nombre', 'Nombre')}
        {texto('nombre_largo', 'Nombre largo (opcional)')}
        {texto('nombre_corto', 'Iniciales')}
        {texto('del', 'Cómo se dice «de…»', { placeholder: 'de la Clínica Alfa' })}
        {texto('mencion', 'Cómo se nombra en una lista', { placeholder: 'la Clínica' })}
        {texto('color', 'Color', { placeholder: '#1D4ED8' })}
        {texto('color_texto', 'Color de la letra (en sólido)', { placeholder: 'vacío: fondo claro' })}
        {texto('color_punto', 'Color del punto (opcional)')}
        {texto('orden', 'Orden')}
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={Boolean(f.externa)} onChange={pon('externa')} data-testid="marca-form-externa" />
          Procedencia externa (una plataforma de terceros: no tiene buzón)
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={Boolean(f.activa)} onChange={pon('activa')} data-testid="marca-form-activa" />
          Activa
        </label>
      </div>
      {f.externa ? (
        <div className="space-y-0.5" data-testid="marca-form-sin-leads-bloque">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={Boolean(f.sin_leads)} onChange={pon('sin_leads')}
              data-testid="marca-form-sin_leads" />
            Sus contactos no se apuntan en Leads
          </label>
          <p className="text-xs text-muted-foreground" data-testid="marca-form-sin-leads-ayuda">
            Un contacto nuevo que saldría como de esta procedencia (por «de dónde viene», la
            procedencia o la marca) no entra, lo mande quien lo mande: la pantalla, el conector o
            un formulario web. Lo que ya está en Leads no se toca.
          </p>
        </div>
      ) : null}
      {!f.externa ? (
        <div className="space-y-2" data-testid="marca-form-mensajes-paciente">
          <h5 className="text-sm font-medium">Mensajes al paciente</h5>
          <p className="text-xs text-muted-foreground">
            Salen en la confirmación y el recordatorio de las citas de las agendas de esta marca
            (Gestión de agendas → Configurar agenda → Marca), con el nombre «Nombre largo
            (Iniciales)». Lo que dejes vacío sale de los datos de la clínica en Configuración.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {texto('direccion_citas', 'Dirección de las citas', {
              placeholder: 'vacío: la de Configuración',
              ayuda: 'Solo en las citas presenciales: la cita online no lleva dirección.' })}
            {texto('telefono_citas', 'Teléfono de contacto', {
              placeholder: 'vacío: el de Configuración',
              ayuda: 'El que se da para cambiar o anular la cita.' })}
          </div>
        </div>
      ) : null}
      {!f.externa ? (
        <div className="space-y-2" data-testid="marca-form-membrete">
          <h5 className="text-sm font-medium">Membrete de los documentos</h5>
          <p className="text-xs text-muted-foreground">
            Salen en los documentos de los médicos con esta marca (Catálogo QX → la ficha del
            cirujano, o «Marca de los documentos de los demás médicos» en Configuración). El nombre
            es el nombre largo; el logotipo, el de la marca. Colores: «Color» es el de la banda y los
            títulos; «Color de la letra» es la letra sobre la banda (sin él, la cabecera va sobre
            blanco); «Color del punto», los filetes y las viñetas. Lo que dejes vacío no sale.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {texto('lema', 'Lema (en cursiva, debajo del nombre)', { placeholder: 'opcional' })}
            {texto('web', 'Web', { placeholder: 'ejemplo.com' })}
            {texto('direccion_documentos', 'Dirección de los documentos', {
              placeholder: 'vacío: la dirección de las citas' })}
            {texto('telefono_documentos', 'Teléfono de los documentos, tal cual', {
              placeholder: 'vacío: «Tel.» y el teléfono de las citas',
              ayuda: 'Con su etiqueta, como se quiera leer: «Tel. / WhatsApp …».' })}
            {texto('pie_documentos', 'Línea del pie', {
              placeholder: 'vacío: la dirección y el teléfono',
              ayuda: 'Debajo del nombre, al pie de cada hoja; la web va en la línea siguiente.' })}
          </div>
        </div>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {!f.externa ? texto('buzon', 'Buzón de Mail', { placeholder: 'clinica',
          ayuda: 'La clave del buzón de la bandeja Mail: quien escribe ahí es de esta marca.' }) : <div />}
        {lista('alias', 'Palabras que la reconocen en «de dónde viene»',
          'Una por línea. Entre barras, una expresión regular. No pongas el apellido del doctor si opera en todas.')}
        {lista('dominios', 'Dominios web', 'Uno por línea: ejemplo.com')}
        {lista('remitentes_web', 'Remitentes de los formularios web',
          'Uno por línea: el dominio (web.clinica.example) o la dirección completa '
          + '(formulario@web.clinica.example) desde la que llegan al buzón los formularios de su web. '
          + 'Si uno pide cita, el lead se crea solo en Leads. Vacío: no se crea ninguno. '
          + 'Si ese dominio es también el de vuestro correo, pon la dirección completa.')}
        {lista('paginas_facebook', 'Páginas de Facebook (id)', 'La página del formulario decide la marca del lead.')}
        {lista('formularios_meta', 'Formularios de Meta (id)', 'Se leen cada media hora si no llega el aviso.')}
        {lista('cuentas_leer', 'Cuentas publicitarias que se leen cada día', 'act_… o solo las cifras.')}
        {lista('cuentas_marca', 'Otras cuentas publicitarias suyas (no se leen)')}
      </div>
      <div className="flex gap-2">
        <Button type="button" onClick={guardar} disabled={guardando} data-testid="marca-form-guardar">
          {guardando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Guardar
        </Button>
        <Button type="button" variant="outline" onClick={onCancelar}>Cancelar</Button>
      </div>
    </div>
  );
}

const TRAT_VACIO = { clave: '', nombre: '', formulario: '', palabras: '', requiere: '', orden: 0, activo: true };

function FormularioTratamiento({ tratamiento, onGuardado, onCancelar }) {
  const nuevo = !tratamiento?.id;
  const [f, setF] = useState(() => (tratamiento
    ? { ...TRAT_VACIO, ...tratamiento, palabras: lineas(tratamiento.palabras), requiere: lineas(tratamiento.requiere) }
    : { ...TRAT_VACIO }));
  const [guardando, setGuardando] = useState(false);
  const pon = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const guardar = async () => {
    setGuardando(true);
    const datos = {
      clave: f.clave.trim(), nombre: f.nombre.trim(), formulario: f.formulario.trim(),
      palabras: deLineas(f.palabras), requiere: deLineas(f.requiere),
      orden: Number(f.orden) || 0, activo: Boolean(f.activo),
    };
    try {
      if (nuevo) await api.post('/api/tratamientos-campana', datos);
      else await api.put(`/api/tratamientos-campana/${tratamiento.id}`, datos);
      toast.success('Tratamiento guardado');
      await onGuardado();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar el tratamiento.'));
    } finally {
      setGuardando(false);
    }
  };
  return (
    <div className="rounded-md border p-3 space-y-3" data-testid="tratamiento-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Campo id="trat-nombre" etiqueta="Nombre («Campaña …»)">
          <Input id="trat-nombre" value={f.nombre} onChange={pon('nombre')} data-testid="tratamiento-form-nombre" />
        </Campo>
        <Campo id="trat-clave" etiqueta="Clave" ayuda="Vacía: se saca del nombre. No se cambia después.">
          <Input id="trat-clave" value={f.clave} onChange={pon('clave')} disabled={!nuevo} />
        </Campo>
        <Campo id="trat-formulario" etiqueta="Respuesta del formulario de Meta que lo elige">
          <Input id="trat-formulario" value={f.formulario} onChange={pon('formulario')} />
        </Campo>
        <Campo id="trat-orden" etiqueta="Orden">
          <Input id="trat-orden" value={f.orden} onChange={pon('orden')} />
        </Campo>
        <Campo id="trat-palabras" etiqueta="Palabras que lo nombran" ayuda="Una por línea; entre barras, expresión regular.">
          <Textarea id="trat-palabras" rows={3} value={f.palabras} onChange={pon('palabras')} data-testid="tratamiento-form-palabras" />
        </Campo>
        <Campo id="trat-requiere" etiqueta="Solo cuenta si además casa con (opcional)">
          <Textarea id="trat-requiere" rows={3} value={f.requiere} onChange={pon('requiere')} />
        </Campo>
      </div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={Boolean(f.activo)} onChange={pon('activo')} /> Activo
      </label>
      <div className="flex gap-2">
        <Button type="button" onClick={guardar} disabled={guardando} data-testid="tratamiento-form-guardar">
          <Save className="w-4 h-4 mr-1" /> Guardar
        </Button>
        <Button type="button" variant="outline" onClick={onCancelar}>Cancelar</Button>
      </div>
    </div>
  );
}

export default function MarcasPanel() {
  const [marcas, setMarcas] = useState(null);
  const [tratamientos, setTratamientos] = useState([]);
  const [formularioMeta, setFormularioMeta] = useState(FORMULARIO_META_VACIO);
  const [editando, setEditando] = useState(null);      // marca (o {} para una nueva)
  const [editandoT, setEditandoT] = useState(null);    // tratamiento (o {})
  const [fallo, setFallo] = useState('');
  const [subiendo, setSubiendo] = useState('');

  const cargar = useCallback(async () => {
    try {
      const [rm, rt] = await Promise.all([
        api.get('/api/marcas/editar'), api.get('/api/tratamientos-campana')]);
      setMarcas(Array.isArray(rm?.data?.marcas) ? rm.data.marcas : []);
      setTratamientos(Array.isArray(rt?.data?.tratamientos) ? rt.data.tratamientos : []);
      const fm = rt?.data?.formulario_meta || {};
      setFormularioMeta(Object.fromEntries(
        LISTAS_FORMULARIO_META.map(({ clave }) => [clave, lineas(fm[clave])])));
      setFallo('');
    } catch (e) {
      setFallo(mensajeError(e, 'No se han podido cargar las marcas.'));
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const subirLogo = async (m, archivo) => {
    if (!archivo) return;
    setSubiendo(m.id);
    try {
      const fd = new FormData();
      fd.append('file', archivo);
      await api.post(`/api/marcas/${m.id}/logo`, fd);
      toast.success('Logotipo subido');
      olvidarMarcas();
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido subir el logotipo.'));
    } finally {
      setSubiendo('');
    }
  };
  const borrarTratamiento = async (t) => {
    try {
      await api.delete(`/api/tratamientos-campana/${t.id}`);
      toast.success('Tratamiento quitado');
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido quitar el tratamiento.'));
    }
  };
  const guardarFormularioMeta = async () => {
    try {
      await api.put('/api/tratamientos-campana/formulario-meta', Object.fromEntries(
        LISTAS_FORMULARIO_META.map(({ clave }) => [clave, deLineas(formularioMeta[clave])])));
      toast.success('Respuestas del formulario guardadas');
    } catch (e) {
      toast.error(mensajeError(e, 'No se han podido guardar las respuestas.'));
    }
  };

  return (
    <SectionCard title="Marcas" className="lg:col-span-2"
      action={<Button type="button" size="sm" variant="outline" onClick={() => setEditando({})}
        data-testid="marcas-nueva"><Plus className="w-4 h-4 mr-1" /> Nueva marca</Button>}>
      <div className="space-y-4" data-testid="marcas-panel">
        <p className="text-sm text-muted-foreground">
          Las marcas de la clínica y las plataformas de terceros por las que llegan contactos. Con
          ellas se sabe de dónde viene cada lead y de quién es cada campaña. Sin ninguna, los
          leads salen «Sin identificar».
        </p>
        {fallo ? <p className="text-sm text-red-700" data-testid="marcas-fallo">{fallo}</p> : null}
        {editando ? (
          <FormularioMarca marca={editando.id ? editando : null}
            onCancelar={() => setEditando(null)}
            onGuardada={async () => { setEditando(null); await cargar(); }} />
        ) : null}
        {marcas === null ? <p className="text-sm text-muted-foreground">Cargando…</p> : null}
        {marcas && !marcas.length ? (
          <p className="text-sm text-muted-foreground" data-testid="marcas-vacio">No hay ninguna marca todavía.</p>
        ) : null}
        {/* Solo informa: sin remitentes, los leads de la web se apuntan a mano. */}
        {marcas && marcas.length && !marcas.some((m) => (m.remitentes_web || []).length) ? (
          <p className="text-xs text-muted-foreground" data-testid="marcas-sin-formularios-web">
            Ninguna marca tiene remitentes de formularios web: los contactos que llegan por la web
            se apuntan a mano en Leads.
          </p>
        ) : null}
        <ul className="space-y-2">
          {(marcas || []).map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2"
              data-testid={`marca-${m.clave}`}>
              <MarcaLead marca={m.clave} marcas={marcas} />
              <span className="text-xs text-muted-foreground">
                {m.externa ? 'procedencia externa' : (m.buzon ? `buzón «${m.buzon}»` : 'sin buzón')}
                {m.externa && m.sin_leads ? ' · sus contactos no se apuntan en Leads' : ''}
                {(m.remitentes_web || []).length
                  ? ` · formularios web: ${m.remitentes_web.join(', ')}` : ''}
                {m.activa ? '' : ' · desactivada'}
              </span>
              <span className="ml-auto flex items-center gap-2">
                <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                  {subiendo === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {m.logo_file_id ? 'Cambiar logotipo' : 'Subir logotipo'}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => subirLogo(m, e.target.files?.[0])} />
                </label>
                <Button type="button" size="sm" variant="ghost" onClick={() => setEditando(m)}
                  data-testid={`marca-editar-${m.clave}`}><Pencil className="w-4 h-4" /></Button>
              </span>
            </li>
          ))}
        </ul>

        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Tratamientos de las campañas</h4>
            <Button type="button" size="sm" variant="outline" onClick={() => setEditandoT({})}
              data-testid="tratamientos-nuevo"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Cada uno da una procedencia «Campaña …» en Leads. Los tratamientos nuevos que se den de
            alta en Campañas se suman solos.
          </p>
          {editandoT ? (
            <FormularioTratamiento tratamiento={editandoT.id ? editandoT : null}
              onCancelar={() => setEditandoT(null)}
              onGuardado={async () => { setEditandoT(null); await cargar(); }} />
          ) : null}
          <ul className="text-sm divide-y" data-testid="tratamientos-lista">
            {tratamientos.map((t) => (
              <li key={t.id || t.clave} className="flex items-center gap-2 py-1">
                <span className="font-medium">Campaña {t.nombre}</span>
                {t.formulario ? <span className="text-xs text-muted-foreground">· «{t.formulario}»</span> : null}
                {t.activo ? null : <span className="text-xs text-muted-foreground">· desactivado</span>}
                <span className="ml-auto flex gap-1">
                  <Button type="button" size="sm" variant="ghost" onClick={() => setEditandoT(t)}><Pencil className="w-4 h-4" /></Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => borrarTratamiento(t)}><Trash2 className="w-4 h-4" /></Button>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t pt-3 space-y-2">
          <h4 className="font-medium text-sm">Respuestas del formulario de Meta</h4>
          <p className="text-xs text-muted-foreground">
            Cómo se escriben las respuestas de las preguntas de tratamiento, de presupuesto, de qué
            quiere mejorar, de cómo prefiere la valoración, de si le parece bien pagarla y de cuándo
            quiere operarse (Meta las manda a veces como clave). Una por línea.
          </p>
          <p className="text-xs text-muted-foreground" data-testid="formulario-meta-ofertas-ayuda">
            Las dos de ofertas dicen qué significa cada respuesta a la pregunta de si quiere recibir
            ofertas: quien elige una de «sí» entra en la lista de ofertas de Marketing; una de «no»
            no entra (o se da de baja). Si se quedan vacías, una respuesta que empieza por «Sí» cuenta
            como sí, una que lleva «no» cuenta como no, y lo demás no entra.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {LISTAS_FORMULARIO_META.map(({ clave, etiqueta }) => (
              <Campo key={clave} id={`formulario-meta-${clave}`} etiqueta={etiqueta}>
                <Textarea id={`formulario-meta-${clave}`} rows={4} value={formularioMeta[clave]}
                  data-testid={`formulario-meta-${clave}`}
                  onChange={(e) => setFormularioMeta({ ...formularioMeta, [clave]: e.target.value })} />
              </Campo>
            ))}
          </div>
          <Button type="button" size="sm" onClick={guardarFormularioMeta} data-testid="formulario-meta-guardar">
            <Save className="w-4 h-4 mr-1" /> Guardar respuestas
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
